/**
 * PlanRows
 *
 * The plan comparison on the Billing page (US-58, BR-113): one row per plan in a single vertical stack at
 * every width, each showing the per-unit price, the listing fee with its period and the agent
 * support price, or "Not offered on this plan". Rows carry no per-plan description or feature list:
 * what every plan includes is the shared PlanIncludedList beneath them.
 */

import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { formatUsd, type BundleTemplate } from '../../api/billing';

const NOT_OFFERED = 'Not offered on this plan';

/** "$10.00 per listing · 30 days", "Free", or "Not offered on this plan". */
function listingText(bundle: BundleTemplate): string {
  if (bundle.listingFeeCents === null) return NOT_OFFERED;
  if (bundle.listingFeeCents === 0) return 'Free';
  const fee = `${formatUsd(bundle.listingFeeCents, { alwaysDecimals: true })} per listing`;
  return bundle.listingPeriodDays ? `${fee} · ${bundle.listingPeriodDays} days` : fee;
}

/** "$4.00 / unit / month", or "Not offered on this plan". */
function agentText(bundle: BundleTemplate): string {
  return bundle.agentUnitPriceCents === null
    ? NOT_OFFERED
    : `${formatUsd(bundle.agentUnitPriceCents, { alwaysDecimals: true })} / unit / month`;
}

function PriceLine({ label, value, testId }: { label: string; value: string; testId: string }) {
  return (
    <div className="flex flex-wrap gap-x-2 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="m-0 text-foreground" data-testid={testId}>
        {value}
      </dd>
    </div>
  );
}

interface PlanRowsProps {
  bundles: BundleTemplate[];
  onSelect: (bundleId: string) => void;
}

/** The row card treatment, shared with the skeleton so nothing jumps when the rows arrive. */
const ROW_CLASS =
  'flex flex-col gap-4 rounded-lg border border-border bg-card p-5 sm:flex-row sm:items-center sm:justify-between';

/** Row-shaped placeholders: name, price line, two detail lines, and the button (right at sm+). */
export function PlanRowsSkeleton({ rows = 2 }: { rows?: number }) {
  return (
    <ul className="m-0 flex list-none flex-col gap-3 p-0" data-testid="plan-rows-loading" aria-busy="true">
      {Array.from({ length: rows }, (_, i) => (
        <li key={i} className={ROW_CLASS}>
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <Skeleton className="h-5 w-36" />
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-56 max-w-full" />
            <Skeleton className="h-4 w-44 max-w-full" />
          </div>
          <Skeleton className="h-11 w-full sm:w-32" />
        </li>
      ))}
    </ul>
  );
}

export function PlanRows({ bundles, onSelect }: PlanRowsProps) {
  return (
    <ul className="m-0 flex list-none flex-col gap-3 p-0" data-testid="plan-rows">
      {bundles.map((bundle) => (
        <li key={bundle.id} data-testid={`plan-row-${bundle.id}`} className={ROW_CLASS}>
          <div className="flex min-w-0 flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="m-0 text-base font-semibold text-foreground">{bundle.name}</h3>
              {bundle.isCurrent && (
                <Badge variant="success" data-testid={`plan-current-badge-${bundle.id}`}>
                  Current
                </Badge>
              )}
            </div>
            <p className="m-0 text-foreground" data-testid={`plan-price-${bundle.id}`}>
              <span className="text-lg font-semibold">
                {formatUsd(bundle.unitPriceCents, { alwaysDecimals: true })}
              </span>
              <span className="text-sm text-muted-foreground"> / unit / month</span>
              {bundle.durationDays ? (
                <span className="text-sm text-muted-foreground"> per {bundle.durationDays} days</span>
              ) : null}
            </p>
            <dl className="m-0 flex flex-col gap-1">
              <PriceLine label="Listing" value={listingText(bundle)} testId={`plan-listing-${bundle.id}`} />
              <PriceLine label="Agent support" value={agentText(bundle)} testId={`plan-agent-${bundle.id}`} />
            </dl>
          </div>
          <Button
            type="button"
            className="min-h-[44px] w-full sm:w-auto"
            data-testid={`plan-select-${bundle.id}`}
            disabled={bundle.isCurrent}
            onClick={() => onSelect(bundle.id)}
          >
            {bundle.isCurrent ? (
              'Current plan'
            ) : (
              <>
                Select plan<span className="sr-only"> {bundle.name}</span>
              </>
            )}
          </Button>
        </li>
      ))}
    </ul>
  );
}
